'use client';

import * as React from 'react';
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';

import { cn } from '@/lib/utils';

const Tabs = TabsPrimitive.Root;

/**
 * `underline` (default): dashed-baseline tabs with a brand underline on the
 * active tab. `segmented`: bordered track on the panel surface with the active
 * tab carried by a brand-tinted pill (Figma 1341:10608, "New Tabs"). The track
 * used to be a grey `ds-gray-subtlest` fill with the active tab raised white on
 * a shadow, which is the stock shadcn look and not ours: it marked selection
 * with elevation and left the ink neutral, so a segmented control read as
 * chrome rather than as the brand. Selection is now brand ink on a brand fill,
 * matching what `line` already did. `line`: clean per-tab pill + flush
 * brand underline, mirroring the page-level `PageTabs` look (no container
 * chrome, no gap between pill and underline). `stat`: full-width row of
 * equal summary tiles that ARE the tabs (label + figure composed by the
 * caller): every tile sits on the panel surface and the active one is marked by
 * a brand rule along its top edge plus darker ink, so the row reads as content.
 * The caller supplies the column count (`className="grid-cols-3"`). Pass the
 * same variant to TabsList and its TabsTriggers.
 *
 * Inactive tiles used to take `bg-ds-gray-subtlest`, which is the exact token
 * card headers use for their chrome band. Directly under a header the two fused
 * into one grey L-shape with the active tile punched out of it as a white hole,
 * so the card looked like it had two headers and no clear hierarchy. Fill is now
 * reserved for chrome: grey means card furniture, surface means data.
 */
type TabsVariant = 'underline' | 'segmented' | 'line' | 'stat';
/** `sm` shrinks the `segmented` variant for dense headers/toolbars. */
type TabsSize = 'default' | 'sm';

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsPrimitive.List.Props & {
    variant?: TabsVariant;
    size?: TabsSize;
  }
>(({ className, variant = 'underline', size = 'default', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      variant === 'stat'
        ? 'grid w-full border-b border-ds-border-subtlest'
        : variant === 'segmented'
          ? cn(
              // p-[3px], not p-1: Figma insets the pill 4px from the outer edge and draws
              // the 1px stroke INSIDE that, so 3px of padding + 1px border reproduces
              // the 36px control. p-1 plus a border renders 38px.
              'inline-flex w-fit items-center gap-0.5 rounded-lg border border-ds-border-subtlest bg-ds-surface-white p-[3px] text-ds-text-subtle',
              size === 'sm' && 'rounded-md p-0.5'
            )
          : variant === 'line'
            ? 'inline-flex w-fit items-center gap-1 text-ds-text-subtle'
            : 'inline-flex w-fit items-end justify-start gap-2 border-b border-dashed border-ds-border-subtlest text-ds-text-subtle',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Tab>,
  TabsPrimitive.Tab.Props & {
    variant?: TabsVariant;
    size?: TabsSize;
  }
>(
  (
    { className, children, variant = 'underline', size = 'default', ...props },
    ref
  ) => {
    if (variant === 'stat') {
      return (
        <TabsPrimitive.Tab
          ref={ref}
          className={cn(
            'group relative flex min-w-0 cursor-pointer flex-col items-start gap-1 border-l border-ds-border-subtlest bg-ds-surface-white px-6 py-3 text-left transition-colors first:border-l-0',
            'hover:bg-ds-brand-lightest',
            // Brand rule along the top of the selected tile, which is the only
            // state marker: every tile shares the panel's surface, so selection
            // is carried by the rule and by the ink weight the caller applies
            // through `group-data-active:`, not by a fill.
            "data-active:before:absolute data-active:before:inset-x-0 data-active:before:top-0 data-active:before:h-0.5 data-active:before:bg-ds-brand-subtle data-active:before:content-['']",
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-border-subtle disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
            className
          )}
          {...props}
        >
          {children}
        </TabsPrimitive.Tab>
      );
    }
    if (variant === 'segmented') {
      return (
        <TabsPrimitive.Tab
          ref={ref}
          className={cn(
            'inline-flex h-7 items-center justify-center whitespace-nowrap rounded-md border border-transparent px-3 ds-text-body-sm-medium text-ds-text-subtle transition-colors',
            size === 'sm' && 'h-5 rounded-sm px-2 ds-text-body-xs-medium',
            'hover:text-ds-text-default',
            'data-active:border-ds-brand-subtlest data-active:bg-ds-brand-lightest data-active:text-ds-brand-subtle',
            // The active tab keeps its brand ink on hover. Without this the
            // generic hover above would drop it back to grey mid-interaction.
            'data-active:hover:text-ds-brand-subtle',
            'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
            className
          )}
          {...props}
        >
          {children}
        </TabsPrimitive.Tab>
      );
    }
    if (variant === 'line') {
      return (
        <TabsPrimitive.Tab
          ref={ref}
          className={cn(
            'group relative -mb-px inline-flex items-center border-b-2 border-transparent p-1 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
            'data-active:border-ds-brand-subtle',
            className
          )}
          {...props}
        >
          <span
            className={cn(
              'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 ds-text-body-sm-medium text-ds-text-subtle transition-colors',
              'group-hover:text-ds-text-darker',
              'group-data-active:bg-ds-brand-subtlest group-data-active:text-ds-brand-subtle'
            )}
          >
            {children}
          </span>
        </TabsPrimitive.Tab>
      );
    }
    return (
      <TabsPrimitive.Tab
        ref={ref}
        className={cn(
          'group relative inline-flex flex-col items-stretch justify-end whitespace-nowrap border-b-2 border-transparent ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
          'data-active:pb-1 data-active:border-ds-brand-default data-active:mb-[-1px]',
          className
        )}
        {...props}
      >
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-md px-2 text-sm font-medium leading-[1.45] text-ds-text-subtle transition-colors',
            'group-hover:text-ds-text-default',
            'group-data-active:bg-ds-brand-subtlest group-data-active:text-ds-brand-default',
            'pt-[5px] pb-[10px] group-data-active:py-[5px]'
          )}
        >
          {children}
        </span>
      </TabsPrimitive.Tab>
    );
  }
);
TabsTrigger.displayName = TabsPrimitive.Tab.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Panel>,
  TabsPrimitive.Panel.Props
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Panel
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Panel.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
